import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SortableBlockViewHost
 *
 * Thin wrapper around `BlockViewContainer` that participates in a
 * parent dnd-kit `SortableContext`. Mounted on every block card
 * inside a Stream surface that sets `surfaceKey`.
 *
 * Drag is NOT a reorder gesture: dropping a block onto another block
 * doesn't reflow the grid, and the dragged card's neighbors stay
 * perfectly still during the gesture (the parent SortableContext
 * uses a no-op sorting strategy). What the drag DOES do depends on
 * the release target:
 *   - Released on a Stack tile / unfurled stack-member → moves the
 *     block into that Stack (existing semantics).
 *   - Released on another regular block AND the surface opted into
 *     `stackOnDropParentId` (layer-1 Collection detail) → creates a
 *     new Stack containing both blocks.
 *   - Released anywhere else → silent no-op.
 *
 * The sortable transform lives on the WRAPPER element, not on
 * `.block-view`, so the card's built-in transforms (the `:active`
 * scale-press, etc.) stay untouched. The wrapper IS the direct
 * child of `.stream-group__cards` — that's the DOM node the
 * stack-unfurl FLIP also writes inline transforms to. Sortable is
 * suspended while a stack is opening / closing in the same group
 * (the parent SortableContext sets `disabled: true`), so the two
 * never write to `style.transform` simultaneously.
 *
 * Drag visual is intentionally restrained per the design brief: a
 * subtle halo + z-index lift on the dragged card; a stack-tile-like
 * glow on the hovered target card (when stack-on-drop is enabled).
 * No scale, rotation, or color change on the dragged card itself.
 */
import React, { memo, useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BlockViewContainer } from "@/components/block/block-view-container";
import { useApp } from "@/state/app-context";
import "./sortable-block-view-host.scss";
// Visual offset per rank for the companion deck. Each companion sits
// this many px down-right of the rank above it, so the stack reads as
// a slightly-fanned deck of cards. Capped at MAX_FAN_RANK so extreme
// selections (20+ blocks) don't trail off-screen — beyond the cap,
// every additional companion stacks at the same depth and the visual
// stays compact.
const COMPANION_FAN_PX = 6;
const MAX_FAN_RANK = 5;
// How long companions hold their release-state class after a multi-
// drag ends. Long enough for the `transition: transform` on
// `.sortable-block--companion` to animate the companion's transform
// from "anchored to leader" back to its rest (`translate(0, 0)`), so
// the deck smoothly returns to the grid instead of teleport-snapping.
// Matches the transition duration in the scss so the class drops the
// instant the animation finishes — any longer and a quick follow-up
// drag would feel sluggish to pick back up.
const COMPANION_RELEASE_MS = 210;
export const SortableBlockViewHost = memo(({ blockId, dateChipMode, }) => {
    // dnd-kit stays armed regardless of multi-select state. The
    // "drag rescue" UX depends on this: if the user holds long
    // enough for `useLongPress` to fire multi-select (~800ms on
    // touch) but then starts moving the finger, `<Stream>`'s
    // `handleDragMove` calls `app.exitMultiselectMode()` and the
    // already-activated drag picks up the motion seamlessly. If
    // the sensor were disabled at multi-select entry, dnd-kit
    // would abort the activated drag and there'd be nothing to
    // "rescue" back into.
    //
    // Taps in multi-select still toggle selection: a tap doesn't
    // produce `onDragMove`, so the exit-on-move path never fires.
    // BlockViewContainer.onClick handles the toggle as before.
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
        id: blockId,
    });
    // All drag-lifecycle data flows through `PouchApp.state.drag` and
    // is consumed via narrow `app.useStore(...)` selectors. The
    // pattern: each selector returns `undefined` (or a stable primitive)
    // for blocks NOT participating in the current drag — Zustand's
    // Object.is gate then bails out the re-render entirely. The
    // actively-dragged block and any multi-drag companions are the
    // only hosts that re-render per drag-move pointer update; the
    // remaining ~99% of cards on screen pay zero render cost.
    const app = useApp();
    // The Stream's `onDragMove` sets `foreignPointerScopeKey` to the
    // scope the pointer is currently inside, IFF that scope is
    // foreign to the active drag's source. Combined with `isDragging`
    // (true only on the actively-dragged item), this tells exactly
    // the dragged card (and only that card) to paint the "drop not
    // allowed" affordance — `cursor: not-allowed` + slight dim —
    // without disturbing its neighbors or the source-day's reflow.
    //
    // `stackOnDropParentId` is set by the Stream on layer-1 Collection
    // surfaces; when set, this card paints a stack-tile-like glow when
    // the dragged block hovers over it (and it isn't itself the
    // dragged card). On other surfaces — homepage feed, Group view,
    // smart collections, inside an open Stack — the glow is suppressed
    // because the drop would be a silent no-op.
    const foreignPointerScopeKey = app.useStore((s) => s.drag.foreignPointerScopeKey);
    const stackOnDropParentId = app.useStore((s) => s.drag.stackOnDropParentId);
    const showForeignScopeCue = isDragging && foreignPointerScopeKey !== undefined;
    const showStackDropTarget = stackOnDropParentId !== undefined && isOver && isDragging === false;
    // Pointer-tracking fallback for the actively-dragged item.
    //
    // `useSortable.transform` returns `null` whenever `over.id`
    // resolves to a droppable that isn't in this SortableContext's
    // items list — Stack tiles and unfurled stack-member cards are
    // non-sortable droppables, so the moment the pointer crosses one
    // of them dnd-kit's `displaceItem` flag flips false and the
    // active card's transform falls to `null`. Left alone, the wrapper
    // would render with `transform: ""` and the card would snap back
    // to its rest position even though the pointer is still moving —
    // exactly the opposite of what the user expects when a valid drop
    // target is glowing under their finger. We substitute the Stream's
    // live `event.delta` (mirrored to `s.drag.liveTransform` on every
    // `onDragMove`) for that null window so the card keeps tracking
    // the pointer until release.
    //
    // Non-dragging blocks return `undefined` from this selector on
    // every move (the `isDragging ? ... : undefined` branch); Object.is
    // bails out their re-render. Only the actively-dragged block ends
    // up subscribed to per-move updates.
    const liveDragTransform = app.useStore((s) => isDragging ? s.drag.liveTransform : undefined);
    // Multi-drag wiring. When a multi-drag is in flight and this block
    // is one of the non-leader selected blocks, `companionInfo` carries
    // the companion's static deck info (rank + initial rect) measured
    // once at drag start. Otherwise the selector returns `undefined`
    // and this branch stays inert. Field-level selectors below pull the
    // leader's initial rect from the same `multi` slice so the
    // anchor-to-leader math can be local — they return stable primitives
    // for non-companion blocks, so Object.is bails their re-render.
    const companionInfo = app.useStore((s) => s.drag.multi?.companions.get(blockId));
    const leaderInitialLeft = app.useStore((s) => s.drag.multi?.activeInitialLeft ?? 0);
    const leaderInitialTop = app.useStore((s) => s.drag.multi?.activeInitialTop ?? 0);
    const companion = companionInfo !== undefined
        ? {
            rank: companionInfo.rank,
            initialLeft: companionInfo.initialLeft,
            initialTop: companionInfo.initialTop,
            leaderInitialLeft,
            leaderInitialTop,
        }
        : undefined;
    const isCompanion = companion !== undefined;
    // `leaderCompanionCount` is the size of the companion deck — non-
    // zero only when THIS block is the leader of a multi-drag. Returns
    // 0 for non-leaders, which bails out the leader-only count badge
    // below.
    const leaderCompanionCount = app.useStore((s) => s.drag.activeBlockId === blockId ? (s.drag.multi?.companions.size ?? 0) : 0);
    const isMultiDragLeader = leaderCompanionCount > 0;
    // Per-move live delta for companions. Returns `undefined` for non-
    // companion blocks (the leader's own move is driven by
    // `useSortable.transform` and the `liveDragTransform` fallback
    // above), so non-companions see stable `undefined` and never
    // re-render on per-move pointer updates. Companions re-render every
    // move so their wrapper transform tracks the leader's live position.
    const companionLiveDelta = app.useStore((s) => isCompanion ? s.drag.liveTransform : undefined);
    // Two pieces of state cover the companion's snap-in / release
    // animation; together they own the "the class is applied before the
    // anchored transform" sequencing that CSS transitions require.
    //
    //   `hasAnchored` — flips from false to true one frame after
    //     `isCompanion` first becomes true. The first render paints
    //     the wrapper with the `.sortable-block--companion` class
    //     active but `transform: ""` (rest), so the CSS transition
    //     rule is in effect when the second render flips the transform
    //     to the anchored value — that's the frame the browser
    //     interpolates from rest → anchored.
    //
    //   `isReleasing` — flips true for `COMPANION_RELEASE_MS` after
    //     `isCompanion` flips back to false. The class is kept active
    //     during this window while the transform inline value drops
    //     to "" (= rest position). The CSS transition animates the
    //     return; the class then drops once the animation completes.
    //
    // Successful drops don't NEED the release path to play out — the
    // cards leave this surface (moved into a stack tile / new stack)
    // and the wrapper unmounts before any release animation could
    // complete. The holdover renders for ~210ms then becomes a no-op;
    // harmless.
    const wasCompanionRef = useRef(false);
    const [hasAnchored, setHasAnchored] = useState(false);
    const [isReleasing, setIsReleasing] = useState(false);
    useEffect(() => {
        if (isCompanion) {
            wasCompanionRef.current = true;
            setIsReleasing(false);
            setHasAnchored(false);
            // Defer anchoring by one frame so the companion class lands in
            // the DOM (and the `transition: transform` rule starts being
            // observed) BEFORE the transform changes to the anchored value.
            // Without this rAF dance the CSS transition wouldn't fire — the
            // first frame would have both the new class AND the new
            // transform applied simultaneously, so the browser would skip
            // straight to the end state without any interpolation.
            const handle = requestAnimationFrame(() => setHasAnchored(true));
            return () => cancelAnimationFrame(handle);
        }
        if (wasCompanionRef.current === false) {
            setHasAnchored(false);
            return;
        }
        wasCompanionRef.current = false;
        setHasAnchored(false);
        setIsReleasing(true);
        const timer = setTimeout(() => setIsReleasing(false), COMPANION_RELEASE_MS);
        return () => clearTimeout(timer);
    }, [isCompanion]);
    // Companion override: when this block is a multi-drag companion, we
    // ignore both `useSortable.transform` (it's null for non-active
    // items under the no-op strategy) AND the live-drag fallback (only
    // the leader uses that path). Instead we compute a custom transform
    // that anchors the companion's top-left to the leader's current
    // top-left, plus a small per-rank fan offset that gives the deck a
    // slight cascade. The math:
    //
    //   leader_current_left = leader_initial_left + delta.x
    //   companion_render_left = leader_current_left + fan(rank).x
    //   transform_x = companion_render_left - companion_initial_left
    //
    // Same form for y. The result: the companion's wrapper paints with
    // its top-left exactly where the leader's top-left is, plus the
    // fan offset. CSS adds a smooth `transition: transform` on the
    // companion class so the snap-to-leader on pickup AND the live
    // tracking both animate (a small lag relative to the leader reads
    // as a "magnetic trail" rather than rigid-body coupling).
    //
    // Non-companion fallback: dnd-kit's `CSS.Transform.toString` returns
    // `undefined` when given a falsy input (null/undefined), so we
    // coalesce to "" below. The "" sentinel matches the companion
    // branch's behavior — an empty inline transform leaves the card at
    // its natural grid position.
    let effectiveTransform;
    // Drag offset published as CSS custom properties on the wrapper
    // (`--drag-offset-x` / `--drag-offset-y`). The `.sortable-block--dragging`
    // pseudo-element placeholder uses these to counter-translate itself
    // back to the card's rest position, so a dotted outline stays
    // anchored to the source slot while the wrapper translates with the
    // pointer. `undefined` when no drag-related transform applies —
    // the placeholder pseudo isn't painted in that case (gated on the
    // `--dragging` / `--companion` class).
    let dragOffset;
    if (isCompanion && companion !== undefined && hasAnchored) {
        const rank = Math.min(companion.rank, MAX_FAN_RANK);
        const fanX = (rank + 1) * COMPANION_FAN_PX;
        const fanY = (rank + 1) * COMPANION_FAN_PX;
        // `companionLiveDelta` is undefined on the very first render of
        // the companion state (Stream seeds `{x: 0, y: 0}` synchronously
        // in `handleDragStart` but the context update reaches this hook
        // on the next commit). Fallback to `{0, 0}` so the companion's
        // first paint anchors to the leader's REST position; the CSS
        // transition on `.sortable-block--companion` then animates the
        // companion→leader delta as a single magnetic snap-in.
        const dx = companionLiveDelta?.x ?? 0;
        const dy = companionLiveDelta?.y ?? 0;
        const x = companion.leaderInitialLeft + dx + fanX - companion.initialLeft;
        const y = companion.leaderInitialTop + dy + fanY - companion.initialTop;
        effectiveTransform = `translate3d(${x}px, ${y}px, 0)`;
        dragOffset = { x, y };
    }
    else if (isCompanion) {
        // First-render-as-companion OR releasing companion: paint at the
        // wrapper's natural rest position so the CSS transition animates
        // from rest → anchored (snap-in) or anchored → rest (release).
        // `useSortable.transform` is null for non-active items under the
        // no-op strategy, so an empty inline transform leaves the card
        // at its natural grid position.
        effectiveTransform = "";
        dragOffset = { x: 0, y: 0 };
    }
    else {
        const fallbackTransform = transform !== null
            ? transform
            : isDragging && liveDragTransform !== undefined
                ? {
                    x: liveDragTransform.x,
                    y: liveDragTransform.y,
                    scaleX: 1,
                    scaleY: 1,
                }
                : null;
        effectiveTransform = CSS.Transform.toString(fallbackTransform) ?? "";
        if (isDragging) {
            dragOffset =
                fallbackTransform !== null
                    ? { x: fallbackTransform.x, y: fallbackTransform.y }
                    : { x: 0, y: 0 };
        }
    }
    // The deck's visual stacking order: leader on top (rank N → highest
    // z-index), then companions in reverse-rank order so rank-0 paints
    // closest to the leader and the deepest companion paints furthest
    // back. The base `.sortable-block--dragging` rule already lifts the
    // leader; the companion z-index is set inline so the rank
    // arithmetic stays close to the rank itself.
    const companionZIndex = isCompanion && companion !== undefined ? 1 - companion.rank : undefined;
    const style = {
        transform: effectiveTransform,
        // Companions ride on a CSS transition (see scss); the regular
        // sortable transition is irrelevant when we've overridden the
        // transform path above. The release window also rides on the
        // companion transition (the class stays put while the inline
        // transform drops to rest, so CSS animates the return).
        transition: isCompanion || isReleasing ? undefined : transition,
        zIndex: companionZIndex,
        // Publishes the wrapper's live drag translation to CSS so the
        // dotted-border placeholder pseudo (paints at the card's rest
        // position while the wrapper rides with the pointer) can apply
        // the inverse translate and stay glued to the source slot.
        ...(dragOffset !== undefined && {
            "--drag-offset-x": `${dragOffset.x}px`,
            "--drag-offset-y": `${dragOffset.y}px`,
        }),
    };
    const className = [
        "sortable-block",
        isDragging ? "sortable-block--dragging" : "",
        showStackDropTarget ? "sortable-block--drop-target" : "",
        isCompanion || isReleasing ? "sortable-block--companion" : "",
        isMultiDragLeader ? "sortable-block--multi-drag-leader" : "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { ref: setNodeRef, style: style, className: className, "data-sortable-block-id": blockId, "data-foreign-scope": showForeignScopeCue ? "" : undefined, "data-drop-target": showStackDropTarget ? "" : undefined, "data-companion-rank": companion?.rank, ...attributes, ...listeners, children: [_jsx(BlockViewContainer, { blockId: blockId, dateChipMode: dateChipMode }), isMultiDragLeader ? (_jsx("div", { className: "sortable-block__multi-count", "aria-hidden": true, children: leaderCompanionCount + 1 })) : null] }));
});
